import { styled, Tooltip } from '@mui/material';

import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';

/**
 * Which month a statement counts for, which is not the month a member would guess.
 *
 * A statement period usually ends mid-month, and a statement is filed under the month its end
 * date falls in. So a member holding one dated May 16 – June 15 looks for "May", finds a row
 * asking for June, and concludes we have asked for a statement they do not have.
 */
const STATEMENT_MONTH_EXPLANATION =
    'The month of the statement is the month of its end date (e.g., your June statement contains transactions through June 15, 2026).';

/**
 * Sized and coloured to sit inside a sentence rather than beside it.
 *
 * `verticalAlign` is load-bearing: an inline SVG sits its bottom edge on the baseline by default,
 * which leaves it riding 3px above the middle of the line. None of the keywords centre it either
 * — they each target a font metric rather than the line box, and every one of those metrics sits
 * below the line's midpoint: `middle` centres on the x-height (0.81px low) and `text-bottom` hangs
 * it off the descender (1.00px low).
 *
 * So the offset is computed instead. `body1` is 16px text on a 22px line, giving an ascent of 14px
 * and a descent of 4px, so the line box centre is 5px above the baseline. The icon is 16px square,
 * so its centre is half that — 8px — above its own bottom edge, and the edge has to drop 3px below
 * the text baseline to put those two centres together. 3px is 0.1875em at this icon's size.
 *
 * Recompute it if `body1` or the icon size changes: `(lineHeight / 2 - descent) - iconSize / 2`.
 */
const HintIcon = styled(InformationCircleIcon)(({ theme }) => ({
    fontSize: theme.spacing(2),
    color: theme.palette.cement[500],
    cursor: 'pointer',
    verticalAlign: '-0.1875em',
}));

/**
 * The ⓘ explaining which month a statement counts for, for use inline after a month label.
 *
 * Pointer and keyboard both reach it. `tabIndex` is what makes the second half true — an `<svg>`
 * takes no focus of its own, so without it the explanation is available only to a mouse. The
 * name comes from `aria-label` rather than `titleAccess` because an SVG `<title>` also draws the
 * browser's own tooltip, which would sit on top of this one.
 *
 * `describeChild` is what carries the explanation to a screen reader. Without it `Tooltip` offers
 * its title as the child's `aria-label`, which the icon's own `aria-label` overrides — leaving the
 * explanation announced to nobody. As a description it is additive: the icon keeps its name and
 * gains `aria-describedby` pointing at the open tooltip.
 */
export default function StatementMonthTooltip() {
    return (
        <Tooltip title={STATEMENT_MONTH_EXPLANATION} placement="bottom" enterTouchDelay={0} describeChild>
            <HintIcon tabIndex={0} role="img" aria-hidden={false} aria-label="What the month of a statement means" />
        </Tooltip>
    );
}
